export type Panel = {
  readonly id: string
  readonly label: string
  readonly badge?: number
  /** Optional cluster label; contiguous same-group panels render under one sub-header. */
  readonly group?: string
  /** When set, the panel is shown in the sidebar only to users with the `admin` role. */
  readonly adminOnly?: boolean
  /** When set, the panel's screen isn't built on live data yet (mock/placeholder). Drives the
   *  sidebar "Demo" tag and is hidden when demo mode is off. Remove the flag when it goes live.
   *  Distinct from the page-level `DemoBadge` (screen built, data seeded) — see NexusShell. */
  readonly demo?: boolean
}

export type Mode = {
  readonly id: string
  readonly label: string
  readonly icon: string
  readonly path: string
  readonly panels: ReadonlyArray<Panel>
  /** Panel the mode lands on. Defaults to panels[0] when unset — set it explicitly so sidebar
   *  order can change without moving the landing route (see NexusShell). */
  readonly defaultPanelId?: string
}

export const MODES: ReadonlyArray<Mode> = [
  {
    id: "portfolio",
    label: "Portfolio",
    icon: "IconBriefcase",
    path: "/portfolio",
    panels: [
      { id: "cockpit", label: "Cockpit", demo: true },
      { id: "decisions", label: "Program Decisions" },
      { id: "financial", label: "Financial Overview", demo: true },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "IconSettings",
    path: "/operations",
    // Landing is pinned to Drop-in Review independent of sidebar order: it is a LIVE screen with
    // real queued work. The previous pin, Approval Queue, is still seed-backed and carries
    // `demo: true`, so both the mode landing and a fresh sign-in opened on a demo screen — and with
    // demo mode toggled OFF the pin pointed at a hidden panel, falling through to whatever sorted
    // first. Keep in step with the root redirect in routes/index.tsx.
    defaultPanelId: "dropin",
    // Clustered into groups, rendered as sub-headers in the sidebar in this order.
    panels: [
      { id: "dropin", label: "Drop-in Review", group: "Intake & Corpus" },
      { id: "intake", label: "Intake Monitor", group: "Intake & Corpus" },
      { id: "corpus", label: "Corpus Library", group: "Intake & Corpus" },
      // Sits directly above My Cleanup, and both stay. The roster answers "what am I answerable
      // for", the queue answers "what must I fix"; the second cannot be inferred from the first,
      // because a folder with no findings produces no queue row and is exactly what a coverage
      // conversation is about. Not demo-gated and not admin-only — like My Cleanup, this has to be
      // reachable by every person who has work in it.
      { id: "my-files", label: "My Files", group: "Intake & Corpus" },
      // Not demo-gated and not admin-only: this is the panel the whole rollout exists for, and
      // it must be reachable by every person who has work in it.
      { id: "cleanup", label: "My Cleanup", group: "Intake & Corpus" },
      { id: "approvals", label: "Approval Queue", group: "Review & Risk", demo: true },
      { id: "diligence", label: "Diligence/Gaps", group: "Review & Risk", demo: true },
      { id: "surveillance", label: "Surveillance", group: "Review & Risk", demo: true },
      { id: "company", label: "Company Dashboard", group: "Programs & Planning", demo: true },
      { id: "dependencies", label: "Dependency Map", group: "Programs & Planning", demo: true },
      { id: "calendar", label: "Release Calendar", group: "Programs & Planning", demo: true },
    ],
  },
  {
    // ADMINISTRATION IS ITS OWN MODE, not a group inside Operations.
    //
    // It was a `group:` sub-header under Operations, which put six admin screens at the bottom of
    // a list of twelve operational ones — so an admin scrolled past everybody's daily work to
    // reach the settings, and a non-admin saw a mode whose lower half simply vanished. As a mode
    // it collapses on its own (the sidebar is an accordion), and the separation says the true
    // thing: these screens change what the system DOES to people, rather than showing what it
    // found.
    id: "administration",
    label: "Administration",
    icon: "IconShieldCog",
    path: "/administration",
    // Every panel is adminOnly, so for a non-admin the whole mode filters out of the sidebar
    // rather than appearing empty.
    panels: [
      { id: "users", label: "Users", adminOnly: true },
      { id: "directory", label: "Directory", adminOnly: true },
      // The roll-up ACROSS people — work reaching nobody — actionable only by someone who can
      // assign areas and exclude accounts.
      { id: "cleanup-overview", label: "Cleanup Overview", adminOnly: true },
      { id: "folder-ownership", label: "Folder Ownership", adminOnly: true },
      // The function-first cut of the same model: that one asks who owns a folder, this asks
      // what a function owns and whether anybody holds it.
      { id: "functions", label: "Function Coverage", adminOnly: true },
      // Enabling one starts a company-wide pass, which is an administrative act regardless of
      // which feature it feeds.
      { id: "jobs", label: "Scheduled Jobs", adminOnly: true },
    ],
  },
  {
    // Read-only reference over the governed data this repo already carries. Not demo-gated (it
    // is real, signed-off data) and not admin-only: a Core Truths card is what every cleanup
    // conversation is measured against, so anyone with work in the queue needs to read one.
    id: "reference",
    label: "Reference",
    icon: "IconBook",
    path: "/reference",
    panels: [{ id: "truth-cards", label: "Truth Cards" }],
  },
  {
    // What the corpus is MISSING, rather than what is in it. Its own mode rather than a pair of
    // Operations panels because the subject is different: every other screen is about documents
    // we hold, and this one is about somebody's personal drive. Keeping that distinction visible
    // in the navigation is part of how the feature stays honest about what it looks at.
    id: "monitoring",
    label: "Knowledge Monitoring",
    icon: "IconRadar",
    path: "/monitoring",
    panels: [
      // INCEPTION is the first AREA, not just the first panel: knowledge at the point it is
      // created, before anything is published. Later areas cover later stages of the same
      // lifecycle, which is why the name is the stage and not the storage. Admin-only and
      // COUNTS ONLY — the roll-up answers "is this working", never "what has Sarah got".
      { id: "inception", label: "Inception", adminOnly: true },
      // Not admin-only and not demo-gated: this is the panel the whole feature exists for, and
      // it must be reachable by every person whose drive is scanned.
      { id: "unpublished", label: "My Unpublished Work" },
      // Admin-only, and the ONE admin screen carrying filenames: deciding what to pull into the
      // corpus is impossible without them. Separate from Inception so the broader view is
      // opened deliberately rather than reached by a careless query.
      { id: "intake", label: "OneDrive Intake", adminOnly: true },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    icon: "IconRobot",
    path: "/agents",
    panels: [
      { id: "fleet", label: "Fleet Dashboard", demo: true },
      { id: "reviews", label: "Quality Reviews", demo: true },
      { id: "hallucinations", label: "Hallucination Monitor", demo: true },
      { id: "prompts", label: "Prompt Control", demo: true },
      { id: "performance", label: "Performance", demo: true },
    ],
  },
  {
    id: "intelligence",
    label: "Intelligence",
    icon: "IconBrain",
    path: "/intelligence",
    panels: [
      { id: "expert", label: "Expert Panel", demo: true },
      { id: "compliance", label: "Compliance & Audit", demo: true },
      { id: "governance", label: "Governance", demo: true },
    ],
  },
]
